// src/components/LogArea.tsx

/**
 * BACKLOG（将来の本命）
 * --------------------------------------------
 * 1) Firestore: users/{uid}/sessions へ永続フラグ追加
 *    - [ ] isArchived: boolean（初期 false）または archivedAt: Timestamp
 *    - 右ペインは isArchived === true のみ表示（状態の永続化）
 *
 * 2) UI: 保存直後カード（非編集ビュー）のアクション強化
 *    - [ ] 📦 アーカイブ（過去一覧へ移動）
 *    - [ ] 🗑️ 削除（確認ダイアログ付き）
 *    - [ ] 🔎 拡大表示（モーダル）
 *    - [ ] 📤 投稿ボタン（共有）
 *    - [ ] 📈 分析ボタン（メタ表示や統計）
 *
 * 3) データ移行/運用
 *    - [ ] 既存セッションの isArchived 初期化（バックフィル）
 *    - [ ] アーカイブ時: isArchived=true / archivedAt=serverTimestamp を書き込み
 *    - [ ] autoRestore/hydrate の抑止は現行センチネルで維持（__NONE__）
 *
 * NOTE:
 *  - 現在は「右ペイン＝current を一時的に非表示 + 重複除去」でUX担保。
 *  - 本命は isArchived を導入してサーバ主導に寄せ、ページ間で一貫性を持たせる。
 */

"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRecordContext } from "@/contexts/RecordContext";
import { useAuthContext } from "@/contexts/AuthContext";
import type { RecordContextType } from "@/contexts/RecordContext";
import type { Session } from "@/contexts/RecordContext";
import type { CardType } from "@/types/Plan";
import type { RecordEntry } from "@/types/Entry";
import { flushSync, createPortal } from "react-dom";
import { createLogger } from "@/lib/logger";
import { useEventLogger } from "@/lib/useEventLogger";
import SessionInputPanel from "@/components/SessionInputPanel";
import TrajectoryMap from "@/components/TrajectoryMap";
import { makeCategoryLayoutStrategy } from "@/lib/trajectoryLayouts";
import { EDITION_DOT_COLORS } from "@/constants/editionColors";
import { SHOW_TRAJECTORY_MAP } from "@/constants/featureFlags";
import { highlightCardWords } from "@/lib/highlightCardText";

const laDbg = createLogger("logArea");
const laLog = (...args: any[]) => laDbg.debug(...args); // 静音可（LOG_NSで点灯）

// Firestore Timestamp / Date / string / number を ISO 文字列へ寄せる（表示とソートの共通ガード）
const toISO = (v: any): string => {
  try {
    if (!v) return "";
    if (typeof v === "string") return v;
    if (v instanceof Date) return v.toISOString();
    if (typeof v === "object" && typeof v?.toDate === "function") return v.toDate().toISOString(); // Firestore Timestamp
    if (typeof v === "object" && "_seconds" in v) return new Date(v._seconds * 1000).toISOString(); // Timestamp-like
    const d = new Date(v);
    return isNaN(d.getTime()) ? "" : d.toISOString();
  } catch {
    return "";
  }
};

// 表示用：壊れてても落ちないローカライズ
const formatDateTime = (v: any): string => {
  const iso = toISO(v);
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" });
};

const RATING_LABELS: Record<number, string> = { 1: "いまいち", 2: "まあまあ", 3: "よかった" };

// 同じお題・同じカードタイプで保存を繰り返したとき、既存エントリを保ったまま
// 新しいエントリを追記／同じカードの更新は上書きする（Rooms機能のsessionId方式を参考・2026-07-08）
function mergeEntriesById(existing: RecordEntry[], incoming: RecordEntry[]): RecordEntry[] {
  const merged = [...existing];
  for (const entry of incoming) {
    const idx = entry.id ? merged.findIndex((e) => e.id === entry.id) : -1;
    if (idx >= 0) {
      merged[idx] = entry;
    } else {
      merged.push(entry);
    }
  }
  return merged;
}

// 8/11・historyPanelTarget：コースページ等が右サイドバー用のDOM要素を渡した場合、
// 保存済みセッション表示（軌跡マップ・発見ボタン等）をそこへportal描画する。
// 渡されなければ（Top・annual2026）従来通りグリッド内にインライン描画＝挙動は完全に不変。
type LogAreaProps = {
  historyPanelTarget?: HTMLElement | null;
  // 2026-08-23・CEO室→CTO室依頼：レッスン④基礎記録機構。保存成功時に呼ばれる任意コールバック
  // （渡されなければ何もしない＝他ページの挙動は完全に不変）。
  onSaveSuccess?: (session: Session) => void;
  // 2026-08-24・CEO室→CTO室依頼：レッスン④の「今回の記録」コンポーネント側で軌跡マップを
  // 自前描画するため、LogArea側の軌跡マップ表示は非表示にする（デフォルトfalse＝他ページは
  // 従来通り表示・挙動不変）。評価ボタン等、軌跡マップの下にあった要素は自然に繰り上がる。
  hideTrajectoryMap?: boolean;
};

const LogArea = ({ historyPanelTarget = null, onSaveSuccess, hideTrajectoryMap = false }: LogAreaProps = {}) => {
  const [expandedSession, setExpandedSession] = useState<Session | null>(null);
  const [posting, setPosting] = useState(false);
  const [postMessage, setPostMessage] = useState<string | null>(null);
  const [discoveryOpenFor, setDiscoveryOpenFor] = useState<string | null>(null);
  const [discoveryText, setDiscoveryText] = useState("");
  const [savingDiscovery, setSavingDiscovery] = useState(false);
  const [discoveryMessage, setDiscoveryMessage] = useState<string | null>(null);
  const [sessionRating, setSessionRating] = useState<number | null>(null);
  const ratedSessionIdRef = useRef<string | null>(null);
  // 2026-07-12・発見ノートフォームが画面下に隠れて気づかれない問題への対応（CEO指示）。
  // フォームが開いたら自動スクロールして視界に入れる。
  const discoveryFormRef = useRef<HTMLDivElement | null>(null);
  const [showNameModal, setShowNameModal] = useState(false);
  const [tempDisplayName, setTempDisplayName] = useState("");
  const [pendingPostSession, setPendingPostSession] = useState<Session | null>(null);
  const { log } = useEventLogger();
  const recordContext = useRecordContext();
  const savedLeftRef = useRef<Session | null>(null);

  // 右ペイン表示用：recordedEntries だけをソースにする（重複除去＋下書き除外）
const recordedEntriesCtx = recordContext?.recordedEntries ?? [];

// recentSession が入ったら常に最新を保持
useEffect(() => {
  if (recordContext?.recentSession) {
    savedLeftRef.current = recordContext.recentSession;
  }
}, [recordContext?.recentSession]);

// セッションが切り替わったら評価をリセット
useEffect(() => {
  const id = recordContext?.recentSession?.sessionId ?? recordContext?.recentSession?.id ?? null;
  if (id && id !== ratedSessionIdRef.current) {
    setSessionRating(null);
  }
}, [recordContext?.recentSession?.sessionId, recordContext?.recentSession?.id]);

// 発見ノートフォームが開いたら自動スクロールして視界に入れる（2026-07-12・CEO指示）
useEffect(() => {
  if (discoveryOpenFor !== null) {
    requestAnimationFrame(() => {
      discoveryFormRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }
}, [discoveryOpenFor]);

// 左ペイン固定表示用：currentSessionId から復元
const pinnedLeft = useMemo<Session | null>(() => {
  const currentId = recordContext?.currentSessionId;
  if (!currentId) return null;

  const found = recordedEntriesCtx.find(
    session => (session.sessionId ?? session.id) === currentId
  );
  return found ?? null;
}, [recordContext?.currentSessionId, recordedEntriesCtx]);

  const profileLoading =
    recordContext.userProfileLoading === true || !recordContext.userProfile;

// 左ペインに出す対象：recentSession > savedLeftRef > pinnedLeft の優先順
const leftSession = recordContext.recentSession ?? savedLeftRef.current ?? pinnedLeft;

// 右ペイン表示用：recordedEntries から作るが、左に固定されている1件は隠す
const rightList = useMemo(() => {
  const baseList = recordedEntriesCtx;

  // 左に固定されている対象（IDと参照の両方で除外する）
  const leftObj = leftSession ?? null;
  const leftFixedId =
    leftObj ? (leftObj.sessionId ?? leftObj.id ?? null) : null;

  const unique = new Map<string, Session>();
  for (const session of baseList) {
    // 参照一致（保存直後、IDがまだ無い楽観オブジェクトを弾く）
    if (leftObj && session === leftObj) continue;

    const id = (session.sessionId ?? session.id ?? "").toString();
    if (!id) continue;
    if (session.isDraft) continue;                  // 右ペインは確定のみ
    if (leftFixedId && id === leftFixedId) continue; // ID一致でも弾く
    if (unique.has(id)) continue;                   // 先勝ちで重複除外
    unique.set(id, session);
  }

  return [...unique.values()].sort((a, b) =>
    toISO(b.date).localeCompare(toISO(a.date))
  );
}, [recordedEntriesCtx, leftSession]);

if (profileLoading) {
    return <div className="text-gray-400">ユーザー情報を取得中...</div>;
  }

  // nullセーフで値を取り出す（※ recordedEntries は上で取り出しているので再定義しない）
  const recordMode = recordContext.recordMode;
  const setRecordMode = recordContext.setRecordMode;
  const draftEntries = recordContext.draftEntries;
  const setDraftEntries = recordContext.setDraftEntries;
  const setRecordedEntries = recordContext.setRecordedEntries;
  const selectedTopic = recordContext.selectedTopic ?? null;
  const selectedCardType: CardType = recordContext.selectedCardType ?? "standard";
  const answerToTopic = recordContext.answerToTopic ?? "";
  const setAnswerToTopic = recordContext.setAnswerToTopic;
  const setRecentSession = recordContext.setRecentSession;

  const { clerkUser } = useAuthContext();
  const currentUserId = clerkUser?.id;
  const displayName =
    recordContext.userProfile?.displayName ||
    clerkUser?.username ||
    clerkUser?.firstName ||
    "";
  
  const canPostLeftSession = !!(leftSession?.sessionId ?? leftSession?.id);
  const postTopicHref =
    leftSession?.topicId
      ? `/posts?topicId=${encodeURIComponent(leftSession.topicId)}`
      : "/posts";
  const postTopicHrefAfterPost =
    leftSession?.topicId
      ? `/posts?topicId=${encodeURIComponent(leftSession.topicId)}&posted=1`
      : "/posts?posted=1";
  
  const sessionHistoryHref = "/sessions";

  const handleRate = async (rating: number) => {
    if (!leftSession) return;
    const id = leftSession.sessionId ?? leftSession.id ?? "";
    setSessionRating(rating);
    ratedSessionIdRef.current = id;
    const cards = (leftSession.entries ?? []).map((e) => e.card).filter(Boolean).slice(0, 3);
    await fetch("/api/session-rating", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId: id,
        topicId: leftSession.topicId ?? null,
        answerToTopic: leftSession.answerToTopic ?? "",
        cards,
        rating,
      }),
    }).catch((e) => console.error("session-rating error", e));
  };

  const handlePostSession = async (session: Session) => {
    if (!session) return;
    if (!currentUserId) {
      setPostMessage("ログイン情報を確認できませんでした");
      return;
    }

    if (!displayName) {
      setPendingPostSession(session);
      setShowNameModal(true);
      return;
    }

    await doPostSession(session, displayName);
  };

  const handleSaveNameAndPost = async () => {
    const name = tempDisplayName.trim();
    if (!name || !pendingPostSession) return;
    await fetch("/api/user-profile", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ displayName: name }),
    });
    await recordContext?.fetchUserProfile?.();
    setShowNameModal(false);
    setTempDisplayName("");
    const session = pendingPostSession;
    setPendingPostSession(null);
    await doPostSession(session, name);
  };

  const doPostSession = async (session: Session, resolvedName: string) => {
    try {
      setPosting(true);
      setPostMessage(null);

      const payload = {
        sessionId: session.sessionId ?? session.id ?? "",
        topicId: session.topicId ?? "",
        topic: session.topic ?? "",
        answerToTopic: session.answerToTopic ?? "",
        cardType: session.cardType ?? "",
        entries: Array.isArray(session.entries) ? session.entries : [],
        interactionType: session.interactionType ?? null,
        displayName: resolvedName,
      };

      const res = await fetch("/api/post-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setPostMessage(data?.error ?? "投稿に失敗しました");
        setTimeout(() => setPostMessage(null), 2000);
        return;
      }

      if (data?.status === "ALREADY_POSTED") {
        setPostMessage("この回答はすでに投稿済みです");
        setTimeout(() => setPostMessage(null), 2000);
        return;
      }

      setPostMessage("投稿しました");
    } catch (e) {
      console.error("post session error", e);
      setPostMessage("投稿に失敗しました");
      setTimeout(() => setPostMessage(null), 2000);
    } finally {
      setPosting(false);
    }
  };

  const handleSaveDiscovery = async (session: Session) => {
    if (!session) return;
    if (!currentUserId) {
      setDiscoveryMessage("ログイン情報を確認できませんでした");
      return;
    }

    const sessionId = session.sessionId ?? session.id ?? "";
    const text = discoveryText.trim();

    if (!sessionId || !text) return;

    try {
      setSavingDiscovery(true);
      setDiscoveryMessage(null);

      const res = await fetch("/api/discoveries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: currentUserId,
          sessionId,
          text,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setDiscoveryMessage(data?.error ?? "発見の保存に失敗しました");
        return;
      }

      setDiscoveryMessage("発見ノートに保存しました");
      setDiscoveryText("");
      // Keep form open so the success message + link remain visible.
    } catch (e) {
      console.error("save discovery error", e);
      setDiscoveryMessage("発見の保存に失敗しました");
    } finally {
      setSavingDiscovery(false);
    }
  };

  // 8/11・右：軌跡マップ＋保存済みセッション（コース右サイドバー化のため変数として切り出し）。
  // historyPanelTargetが渡された場合はcreatePortalでその要素内に描画し、渡されなければ
  // 従来通りグリッド内にインラインで描画する（Top・annual2026は現状維持・挙動不変）。
  const rightColumnJSX = (
  <div className="order-2 md:order-2 md:col-span-5 min-w-0">
    <div className="bg-white border border-gray-200 rounded-md p-4 max-h-[500px] overflow-y-auto">

    {/* 入力中の軌跡マップは2026-07-11、ユーザー混乱防止のため非表示化。
        （直近1点しか出ず、複数回答が「消えた」ように見えるため。保存済みセッション側の
        軌跡（蓄積表示）は維持。再設計後に復活を検討） */}

    {/* 保存直後セッション（Kazuto指摘・7/8：次のカードを「書く」に送ってdraftEntriesが
        再び非空になっても、保存済みの内容を非表示にしない。以前は!draftEntries.lengthで
        隠していたため、次のカードを書き始めた瞬間に直前の保存内容が消えたように見えていた） */}
    {leftSession && (
      <div className="mb-2">
        {/* Content box */}
        <div className="mb-2 p-4 border border-gray-200 bg-white rounded-md">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">保存済みセッション</h3>
          <div className="text-xs text-gray-500 mb-1">
            {formatDateTime(leftSession.date)} ・ {leftSession.cardType}
          </div>
          <div className="text-xs text-gray-600 mb-1">お題: {leftSession.topic}</div>
          {leftSession.answerToTopic && (
            <div className="text-sm mb-2">
              <span className="font-semibold text-gray-700">お題への回答：</span>
              {leftSession.answerToTopic}
            </div>
          )}
          {/* 2026-07-12・丸チップだと本文と切り離されて読みづらい（Kazuto実機指摘）ため、
              文中の単語をその場でハイライトする方式に変更（RoomView.tsxの既存表現に統一） */}
          <ul className="text-sm space-y-1">
            {(leftSession.entries ?? [])
              .filter((entry) => entry.source !== "topic")
              .map((entry, i) => (
                <li key={entry.id ?? i}>
                  {highlightCardWords(entry.text ?? "", entry.card ? [entry.card] : [])}
                </li>
              ))}
          </ul>

          {/* 軌跡マップ（全版共通・2026-07-26稜線ティア統一。一行読み返しは意味座標＋軸表示の
              廃止に伴い非表示化・generateNarrative自体は未使用のまま温存）。
              2026-08-24・レッスン④はhideTrajectoryMap=trueで非表示にし、代わりに
              「今回の記録」コンポーネント側で自前描画する（TASKS_ACTIVE.md 8/24エントリ参照）。
              2026-08-24・Kazuto発案・CEO室評価・Kazuto最終承認：表現形式が「開く/閉じる」という
              質的な軸に直感的に対応しないとの指摘を受け、/sessions・コース・トップの3箇所すべてで
              デフォルト非表示化。SHOW_TRAJECTORY_MAPをtrueに戻すだけで元通り表示できる
              （コードは削除せず温存・src/constants/featureFlags.ts参照）。 */}
          {SHOW_TRAJECTORY_MAP && !hideTrajectoryMap && (
            <div className="mt-3">
              <TrajectoryMap
                entries={leftSession.entries ?? []}
                animate={true}
                layoutStrategy={makeCategoryLayoutStrategy(leftSession.cardType as CardType)}
                color={EDITION_DOT_COLORS[leftSession.cardType as CardType]}
              />
              <div className="mt-1 text-[10px] text-gray-400">
                この軌跡は、あなたの語り方が話を広げたか閉じたかの変化を表します。
              </div>
            </div>
          )}
        </div>

        {/* Rating row */}
        <div className="mb-2">
          {sessionRating === null ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">この回答は？</span>
              {[1, 2, 3].map((v) => (
                <button
                  key={v}
                  onClick={() => handleRate(v)}
                  className="text-xs px-2 py-0.5 rounded border border-gray-200 text-gray-400 hover:border-gray-400 hover:text-gray-600"
                >
                  {RATING_LABELS[v]}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">✓ 「{RATING_LABELS[sessionRating]}」と記録しました</p>
          )}
        </div>

        {/* Action row */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => {
              const sessionId = leftSession?.sessionId ?? leftSession?.id ?? null;
              setDiscoveryMessage(null);
              setDiscoveryText("");
              setDiscoveryOpenFor((prev) => (prev === sessionId ? null : sessionId));
            }}
            disabled={!leftSession}
            className="text-xs px-2 py-0.5 rounded border border-gray-200 text-gray-600 bg-white hover:border-gray-400 hover:text-gray-800 disabled:opacity-40"
          >
            発見
          </button>
          <button
            onClick={() => leftSession && handlePostSession(leftSession)}
            disabled={posting || !leftSession || !canPostLeftSession}
            className="text-xs px-2 py-0.5 rounded border border-blue-200 text-blue-700 bg-blue-50 hover:border-blue-400 hover:bg-blue-100 disabled:opacity-40"
          >
            {posting ? "投稿中..." : "みんなの回答に投稿"}
          </button>
          <button
            onClick={() => leftSession && setExpandedSession(leftSession)}
            className="text-xs px-2 py-0.5 rounded border border-gray-200 text-gray-600 bg-white hover:border-gray-400 hover:text-gray-800"
          >
            拡大
          </button>
          <button
            onClick={() => {
              const recentSavedSession = leftSession;
              laLog("archive:click", {
                id: recentSavedSession?.sessionId ?? recentSavedSession?.id,
              });

              recordContext.skipNextMirror?.();
              recordContext.skipAutoRestoreNextLoad?.();
              recordContext.suppressAutoRestoreOnceRef.current = true;

              if (!recentSavedSession) return;

              const archivedSession: Session = {
                ...recentSavedSession,
                isDraft: false,
                date: new Date().toISOString(),
              };
              const archivedSessionId =
                archivedSession.sessionId ?? archivedSession.id!;

              flushSync(() => {
                recordContext.setRecordedEntries((prev) => {
                  const withoutTarget = prev.filter(
                    (session) => (session.sessionId ?? session.id) !== archivedSessionId
                  );
                  return [archivedSession, ...withoutTarget];
                });
              });

              recordContext.setRecentSession(null);
              recordContext.setCurrentSessionId?.(null);
              savedLeftRef.current = null;
            }}
            className="text-xs px-2 py-0.5 rounded border border-gray-200 text-gray-600 bg-white hover:border-gray-400 hover:text-gray-800"
          >
            アーカイブ
          </button>
        </div>

        {postMessage && (
          <div className="mt-2">
            <div
              className={`text-xs ${
                postMessage.includes("失敗")
                  ? "text-red-600"
                  : postMessage.includes("投稿済み")
                  ? "text-gray-500"
                  : "text-green-600"
              }`}
            >
              {postMessage}
            </div>
            {(postMessage === "投稿しました" ||
              postMessage === "この回答はすでに投稿済みです") && (
              <a
                href={postTopicHrefAfterPost}
                className="mt-1 inline-block text-xs text-blue-600 underline hover:text-blue-800"
              >
                みんなの回答を見る
              </a>
            )}
          </div>
        )}

        {discoveryOpenFor !== null && discoveryOpenFor === (leftSession.sessionId ?? leftSession.id ?? null) && (
          <div ref={discoveryFormRef} className="mt-3 rounded-md border border-gray-200 bg-white p-3">
            {discoveryMessage === "発見ノートに保存しました" ? (
              <div>
                <div className="text-xs text-green-700 mb-2">{discoveryMessage}</div>
                <div className="flex items-center gap-4">
                  <a href="/discoveries" className="text-xs text-gray-500 hover:text-gray-700">
                    発見ノートを見る →
                  </a>
                  <button
                    onClick={() => {
                      setDiscoveryOpenFor(null);
                      setDiscoveryMessage(null);
                    }}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    閉じる
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-sm font-semibold text-gray-700 mb-2">発見を書く</div>
                <textarea
                  rows={4}
                  value={discoveryText}
                  onChange={(e) => setDiscoveryText(e.target.value)}
                  className="w-full rounded border border-gray-200 bg-white p-2 text-base"
                  placeholder="このセッションから気づいたことを、発見ノートに書き残す"
                />
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => leftSession && handleSaveDiscovery(leftSession)}
                    disabled={savingDiscovery || !discoveryText.trim()}
                    className="bg-gray-700 text-white px-3 py-1 rounded text-sm hover:bg-gray-800 disabled:opacity-50"
                  >
                    {savingDiscovery ? "保存中..." : "保存"}
                  </button>
                  <button
                    onClick={() => {
                      setDiscoveryOpenFor(null);
                      setDiscoveryText("");
                      setDiscoveryMessage(null);
                    }}
                    className="bg-gray-100 text-gray-600 px-3 py-1 rounded text-sm hover:bg-gray-200"
                  >
                    キャンセル
                  </button>
                </div>
                {discoveryMessage && (
                  <div className="mt-2 text-xs text-red-600">{discoveryMessage}</div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    )}

    {/* Empty state */}
    {draftEntries.length === 0 && !answerToTopic && !leftSession && (
      <p className="text-sm text-gray-400">
        いま進行中のセッションはありません。<br />カードをめくって、書き始めるとここに表示されます。
      </p>
    )}
    </div>{/* end session container */}
  </div>
  );
  // ↑/右列（rightColumnJSXの定義おわり）

  return (
    <>
    <div className="ktr-rail">{/* ← 共通レール：左右の安全余白＆幅揃え */}
      <div className="w-full p-4 bg-white rounded-md shadow-md border">

        {recordMode && (
          <>
            {/* 上段：SPは縦積み、md以上で 5:5 */}
            <div className="grid grid-cols-1 md:grid-cols-10 gap-4">

  {/* 左：セッション入力（SPでは上） */}
  <div
    className={
      historyPanelTarget
        ? "order-1 md:order-1 md:col-span-10 min-w-0"
        : "order-1 md:order-1 md:col-span-5 min-w-0 md:border-r md:pr-4"
    }
  >
    <div className="p-4 border border-gray-200 bg-white rounded-md">
      <SessionInputPanel />

      {/* 保存・キャンセルボタン（2026-07-12・右寄せ化：入力後のカーソル/文字数カウンターが
          右側にあるため、保存ボタンも右寄せにしてマウス移動を減らす。CEO指示） */}
      {(draftEntries.length > 0 || answerToTopic) && (
        <div className="flex gap-2 mt-4 justify-end">
          <button
            onClick={async () => {
              if (!currentUserId) {
                alert("UID未取得のためセッション保存できません！");
                return;
              }
              if (!draftEntries.length && !answerToTopic) return;

              // 同じお題・同じカードタイプで直前に保存済みのセッションが右ペインに
              // 表示されている場合は、新規文書を作らずそこへ追記する
              // （Kazuto承認・2026-07-08。Rooms機能のsessionId継続方式を参考にした設計）
              const canContinue =
                !!leftSession &&
                (leftSession.topicId ?? null) === (selectedTopic?.id ?? null) &&
                leftSession.cardType === selectedCardType;

              const baseEntries: RecordEntry[] = canContinue
                ? ((leftSession?.entries as RecordEntry[]) ?? [])
                : [];
              const baseSessionId: string | undefined = canContinue
                ? (leftSession?.sessionId ?? leftSession?.id ?? undefined)
                : undefined;

              const mergedEntries = mergeEntriesById(baseEntries, draftEntries as RecordEntry[]);
              const attemptedDraftEntries = draftEntries; // 失敗時のロールバック用（マージ後ではなく入力時点のもの）

              const newSession: Session = {
                entries: mergedEntries,
                date: new Date().toISOString(),
                cardType: selectedCardType,
                topicId: selectedTopic?.id ?? null,
                topic: selectedTopic?.topic ?? "",
                answerToTopic,
                interactionType: selectedTopic?.interactionType || "simple",
                // 継続保存の場合、既存文書が自動保存の下書き（isDraft:true）である可能性があるため、
                // 明示保存であることをここで必ず確定させる（下書きのまま残らないように）
                isDraft: false,
                ...(baseSessionId ? { sessionId: baseSessionId } : {}),
              };

              laLog('save:click', { entriesLen: draftEntries.length, hasAnswerToTopic: !!answerToTopic, continuing: canContinue });

              recordContext.suppressAutoRestoreOnceRef.current = true;

              // 4つのstate更新がバラバラのタイミングで反映されると、
              // 「入力欄は空になったが右セクションはまだ新しい内容に切り替わっていない」
              // 一瞬（＝保存済みセッションが消えて見える）が起きうるため、まとめて同期反映する
              flushSync(() => {
                setRecordedEntries((prev) => {
                  const next = [newSession, ...prev];
                  laLog('save:optimisticApplied', { listCount: next.length });
                  return next;
                });
                setDraftEntries([]);
                setAnswerToTopic("");
                setRecentSession(newSession);
              });

              try {
                const res = await fetch("/api/session-log", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ uid: currentUserId, ...newSession }),
                });
                if (!res.ok) throw new Error("セッション保存に失敗しました");

                const { sessionId } = await res.json();
                laLog('save:serverOk', { sessionId });

                log("session_save", {
                  card_type: selectedCardType,
                  topic: selectedTopic?.topic ?? null,
                  entries: draftEntries.length,
                  has_answer: !!answerToTopic
                });

                const savedSession: Session = { ...newSession, id: sessionId, sessionId };

                flushSync(() => {
                  setRecordedEntries((prev) => {
                    const filtered = prev.filter(s =>
                      s !== newSession && (s.sessionId ?? s.id) !== sessionId
                    );
                    const next = [savedSession, ...filtered];
                    laLog('save:finalApplied', { listCount: next.length, headId: savedSession.sessionId });
                    return next;
                  });
                  setRecentSession(savedSession);
                  savedLeftRef.current = savedSession;
                });

                recordContext.fixCurrentSessionId(sessionId);

                // 2026-08-23・CEO室→CTO室依頼：レッスン④基礎記録機構。保存成功をコース側
                // （StepView）へ知らせる。渡されなければ何もしない＝他ページの挙動は完全に不変。
                onSaveSuccess?.(savedSession);

              } catch (err) {
                setRecordedEntries((prev) => prev.filter((s) => s !== newSession));
                setDraftEntries(attemptedDraftEntries || []);
                setAnswerToTopic(newSession.answerToTopic || "");
                laLog('save:error', err);
                alert("セッション保存エラー");
                console.error(err);
              }
            }}
            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
          >
            保存する
          </button>

          <button
            onClick={() => {
              laLog('draft:cancel');
              setDraftEntries([]);
              setAnswerToTopic("");
              setRecentSession(null);
            }}
            className="bg-gray-200 text-gray-800 px-3 py-1 rounded text-sm hover:bg-gray-300"
          >
            キャンセル
          </button>
        </div>
      )}
    </div>
  </div>{/* /左列 */}

  {!historyPanelTarget && rightColumnJSX}

</div>{/* /grid */}

      {/* 8/11・historyPanelTarget指定時はここでportal描画（DOM上の見た目位置はtarget要素側） */}
      {!!historyPanelTarget && createPortal(rightColumnJSX, historyPanelTarget)}

  {/* 下段：記録一覧への導線（コンテンツがある場合のみ） */}
  {(rightList.length > 0 || draftEntries.length > 0 || !!answerToTopic || !!leftSession) && (
    <div className="mt-6 flex flex-wrap justify-center items-center gap-x-1 gap-y-1 text-xs text-gray-400">
      {rightList.length > 0 && (
        <>
          <a href="/sessions" className="hover:text-gray-600 hover:underline transition-colors">自分の回答</a>
          <span className="select-none">·</span>
        </>
      )}
      <a href="/posts" className="hover:text-gray-600 hover:underline transition-colors">みんなの回答</a>
      <span className="select-none">·</span>
      <a href="/discoveries" className="hover:text-gray-600 hover:underline transition-colors">発見ノート</a>
      <span className="select-none">·</span>
      <a href="/practice" className="hover:text-gray-600 hover:underline transition-colors">実践レポート</a>
    </div>
  )}
        </>
        )}
      </div>
    </div>

    {showNameModal && (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
          <h2 className="text-lg font-bold mb-3">投稿するには表示名の登録が必要です</h2>
          <input
            value={tempDisplayName}
            onChange={(e) => setTempDisplayName(e.target.value)}
            className="w-full border rounded px-3 py-2 mb-4"
            placeholder="表示名"
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") handleSaveNameAndPost(); }}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setShowNameModal(false); setTempDisplayName(""); setPendingPostSession(null); }}
              className="px-4 py-2 bg-gray-200 rounded"
            >
              キャンセル
            </button>
            <button
              onClick={handleSaveNameAndPost}
              disabled={!tempDisplayName.trim()}
              className="px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50"
            >
              保存して投稿
            </button>
          </div>
        </div>
      </div>
    )}

    {expandedSession && (
      <div
        onClick={() => setExpandedSession(null)}
        className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 cursor-zoom-out"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-[90vw] h-[90vh] max-w-4xl p-8 rounded shadow-lg overflow-y-auto"
        >

          <div className="text-xs text-gray-500 mb-2">
            {formatDateTime(expandedSession.date)} ・🃏 {expandedSession.cardType}
          </div>

          <div className="text-sm text-gray-600 mb-3">
            📌 お題: {expandedSession.topic}
          </div>

          {expandedSession.answerToTopic && (
            <div className="text-lg mb-4">
              <span className="font-semibold text-gray-700">
                お題への回答：
              </span>
              {expandedSession.answerToTopic}
            </div>
          )}

          {/* 2026-07-12・丸チップだと本文と切り離されて読みづらく、text-xs固定のため拡大表示では
              文字サイズも本文（text-lg）と食い違っていた（Kazuto実機指摘）。文中の単語をその場で
              ハイライトする方式に変更（RoomView.tsxの既存表現に統一・サイズは本文から自動継承） */}
          <ul className="space-y-2 text-lg">
            {(expandedSession.entries ?? [])
              .filter((entry) => entry.source !== "topic")
              .map((entry, i) => (
                <li key={entry.id ?? i}>
                  {highlightCardWords(entry.text ?? "", entry.card ? [entry.card] : [])}
                </li>
              ))}
          </ul>

        </div>
      </div>
    )}
    </>
  );  
};

export default LogArea;
